import { useEffect, useState } from 'react';
import ModelType from '@/lib/constants/challengeModel/ModelType.enum';
import NotResponsiveAlert from './NotResponsiveAlert';
import MoveModeContext from './MoveModeContext';
import TabsWrapper from './TabsWrapper';
import SelectedModelTypeTabs from './SelectedModelTypeTabs';
import MoveModeButton from './MoveModeButton';
import ModelEditor from './ModelEditor';
import ModelsContext from './ModelsContext';
import LoadingSpinnerWithText from '@/components/LoadingSpinnerWithText';
import { FormattedMessage } from 'react-intl';
import { type EditorModel } from '@/lib/types';
import usePolledSwrAsComplexState from '@/lib/api/usePolledSwrAsComplexState';
import SelectedModelIdContext from './SelectedModelIdContext';
import ModelSelector from './ModelSelector';
import ModelDeactivatedWarning from './ModelDeactivatedWarning';
import EditorIsReadonlyContext from './EditorIsReadonlyContext';
import CreateDefaultModels from './CreateDefaultModels';
import getDefaultModel from './getDefaultModel';

const ChallengeModelsEditor = () => {
  const [loading, models, setModels] = usePolledSwrAsComplexState<
    Record<number, EditorModel>
  >('challenge-models/editor');

  const [selectedModelType, setSelectedModelType] = useState(
    ModelType.CHALLENGE,
  );
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);

  const modelsForSelectedType = Object.values(models ?? {}).filter(
    model => model.type === selectedModelType,
  );
  const hasAnyModels = !loading && modelsForSelectedType.length > 0;

  const resetSelectedModel = (selectedModelType: ModelType): void => {
    const fallbackModelId = modelsForSelectedType[0]?.id;

    setSelectedModelId(
      getDefaultModel(models, selectedModelType)?.id ?? fallbackModelId ?? null,
    );
  };

  useEffect(() => {
    if (models === null) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    resetSelectedModel(selectedModelType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasAnyModels]);

  const [inMoveMode, setInMoveMode] = useState(false);

  if (
    loading ||
    (hasAnyModels &&
      (selectedModelId === null ||
        typeof models[selectedModelId] === 'undefined'))
  ) {
    return (
      <LoadingSpinnerWithText>
        <FormattedMessage id="editor.challenge_model_editor.loading_message" />
      </LoadingSpinnerWithText>
    );
  }

  const modelIsDeactivated =
    selectedModelId === null || models[selectedModelId].deactivatedAt !== null;

  return (
    <ModelsContext.Provider value={[models, setModels]}>
      <NotResponsiveAlert />
      {!hasAnyModels ? (
        <CreateDefaultModels />
      ) : (
        <MoveModeContext.Provider value={inMoveMode && !modelIsDeactivated}>
          <TabsWrapper>
            <SelectedModelTypeTabs
              selectedModelType={selectedModelType}
              setSelectedModelType={modelType => {
                setSelectedModelType(modelType);

                resetSelectedModel(modelType);
              }}
            />
            <MoveModeButton
              onClick={() => setInMoveMode(!inMoveMode)}
              disabled={modelIsDeactivated}
            />
          </TabsWrapper>
          <ModelSelector
            selectedModelType={selectedModelType}
            selectedModelId={selectedModelId!}
            setSelectedModelId={modelId => setSelectedModelId(modelId)}
          />
          {modelIsDeactivated && <ModelDeactivatedWarning />}
          <EditorIsReadonlyContext.Provider value={modelIsDeactivated}>
            <SelectedModelIdContext.Provider value={selectedModelId!}>
              <ModelEditor />
            </SelectedModelIdContext.Provider>
          </EditorIsReadonlyContext.Provider>
        </MoveModeContext.Provider>
      )}
    </ModelsContext.Provider>
  );
};

export default ChallengeModelsEditor;
